<template>
  <el-card>
    <div slot="header" class="toolbar">
      <span>订单信息</span>
    </div>

    <div class="search-bar search-wrap">
      <el-input
        v-model.trim="query.orderNo"
        placeholder="请输入订单号"
        clearable
        style="width: 240px"
        @keyup.enter.native="handleSearch"
      />
      <el-select v-model="query.deliveryType" clearable placeholder="交付类型" style="width: 140px">
        <el-option :value="1" label="实物物流" />
        <el-option :value="2" label="虚拟发放" />
      </el-select>
      <el-select v-model="query.orderStatus" clearable placeholder="订单状态" style="width: 140px">
        <el-option v-for="item in orderStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="reset">重置</el-button>
    </div>

    <el-table :data="list" border style="width: 100%">
      <el-table-column prop="orderNo" label="订单号" width="180" />
      <el-table-column prop="nickname" label="用户昵称" width="140" show-overflow-tooltip />
      <el-table-column prop="phone" label="手机号" width="130" />
      <el-table-column prop="goodsSummary" label="商品摘要" min-width="180" show-overflow-tooltip />
      <el-table-column prop="payPoints" label="支付积分" width="100" align="right" />
      <el-table-column label="折扣信息" width="150" show-overflow-tooltip>
        <template slot-scope="{ row }">
          {{ discountInfoText(row) }}
        </template>
      </el-table-column>
      <el-table-column prop="orderStatus" label="订单状态" width="100" align="center">
        <template slot-scope="{ row }">
          {{ orderStatusText(row.orderStatus) }}
        </template>
      </el-table-column>
      <el-table-column label="发放状态" width="120" align="center">
        <template slot-scope="{ row }">
          {{ issueStatusText(row) }}
        </template>
      </el-table-column>
      <el-table-column prop="afterSaleStatus" label="售后状态" width="100" align="center">
        <template slot-scope="{ row }">
          {{ afterSaleStatusText(row.afterSaleStatus) }}
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="170" />
      <el-table-column label="操作" width="230" fixed="right" align="center">
        <template slot-scope="{ row }">
          <div class="operation-grid">
            <el-button size="mini" plain @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="canViewLogistics(row)"
              size="mini"
              class="logistics-op-btn"
              @click.stop="openLogistics(row)"
            >
              查看物流
            </el-button>

            <el-tooltip
              v-if="$hasPermission('mall:order:delivery')"
              :disabled="canDelivery(row)"
              :content="getDeliveryDisabledReason(row)"
              placement="top"
            >
              <span class="action-button-wrap">
                <el-button
                  size="mini"
                  type="primary"
                  :disabled="!canDelivery(row)"
                  @click="openDelivery(row)"
                >
                  发货
                </el-button>
              </span>
            </el-tooltip>

            <el-tooltip
              v-if="$hasPermission('mall:order:cancel')"
              :disabled="canCancel(row)"
              :content="getCancelDisabledReason(row)"
              placement="top"
            >
              <span class="action-button-wrap">
                <el-button
                  size="mini"
                  type="warning"
                  :disabled="!canCancel(row)"
                  @click="openCancel(row)"
                >
                  取消
                </el-button>
              </span>
            </el-tooltip>

            <el-tooltip
              v-if="$hasPermission('mall:order:after-sale-audit') || $hasPermission('mall:order:after-sale-receive')"
              :disabled="canAuditOrder(row)"
              :content="getOrderAuditDisabledReason(row)"
              placement="top"
            >
              <span class="action-button-wrap operation-grid__wide">
                <el-button
                  size="mini"
                  type="danger"
                  :disabled="!canAuditOrder(row)"
                  @click="openDetail(row, true)"
                >
                  售后
                </el-button>
              </span>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="query.page"
        :page-size="query.limit"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog title="订单详情" :visible.sync="detailVisible" width="1180px" class="order-detail-dialog">
      <div v-if="detail.order">
        <el-descriptions :column="3" border class="order-detail-desc">
          <el-descriptions-item label="订单号">{{ detail.order.orderNo }}</el-descriptions-item>
          <el-descriptions-item label="用户昵称">{{ detail.user ? detail.user.nickname : '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ detail.user ? detail.user.phone : '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">{{ orderStatusText(detail.order.orderStatus) }}</el-descriptions-item>
          <el-descriptions-item label="发放状态">{{ issueStatusText(detail.order) }}</el-descriptions-item>
          <el-descriptions-item label="售后状态">{{ afterSaleStatusText(detail.order.afterSaleStatus) }}</el-descriptions-item>
          <el-descriptions-item label="支付积分">{{ detail.order.payPoints }}</el-descriptions-item>
          <el-descriptions-item label="已退积分">{{ detail.order.refundPoints || 0 }}</el-descriptions-item>
          <el-descriptions-item label="总件数">{{ detail.order.totalQuantity }}</el-descriptions-item>
          <el-descriptions-item label="买家备注" :span="3">{{ detail.order.buyerRemark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="后台备注" :span="3">{{ detail.order.adminRemark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="取消原因" :span="3">{{ detail.order.cancelReason || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="Number(detail.order.deliveryType) === 2" label="虚拟发放内容" :span="3">
            <span class="pre-line-text">{{ virtualIssueContent() }}</span>
          </el-descriptions-item>
        </el-descriptions>

        <div class="section-title">商品明细</div>
        <el-table :data="detail.items || []" border style="width: 100%">
          <el-table-column prop="goodsName" label="商品名称" min-width="180" />
          <el-table-column prop="skuName" label="SKU名称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="specText" label="规格描述" min-width="180" show-overflow-tooltip />
          <el-table-column prop="quantity" label="数量" width="80" align="center" />
          <el-table-column prop="originalPointsPrice" label="原始积分" width="100" align="right" />
          <el-table-column prop="pointsPrice" label="单件积分" width="100" align="right" />
          <el-table-column label="折扣信息" width="150" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ discountInfoText(row) }}
            </template>
          </el-table-column>
          <el-table-column prop="totalPoints" label="总积分" width="100" align="right" />
          <el-table-column prop="afterSaleStatus" label="售后状态" width="100" align="center">
            <template slot-scope="{ row }">
              {{ afterSaleStatusText(row.afterSaleStatus) }}
            </template>
          </el-table-column>
        </el-table>

        <div v-if="detail.address" class="section-title">收货地址</div>
        <el-descriptions v-if="detail.address" :column="2" border>
          <el-descriptions-item label="收货人">{{ detail.address.receiverName }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ detail.address.receiverPhone }}</el-descriptions-item>
          <el-descriptions-item label="地区">{{ `${detail.address.provinceName || ''}${detail.address.cityName || ''}${detail.address.countyName || ''}` }}</el-descriptions-item>
          <el-descriptions-item label="详细地址">{{ detail.address.detailAddress }}</el-descriptions-item>
        </el-descriptions>

        <div class="section-title">物流/发放信息</div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="交付类型">{{ detail.order.deliveryType === 2 ? '虚拟发放' : '实物物流' }}</el-descriptions-item>
          <el-descriptions-item label="发放状态">{{ issueStatusText(detail.order) }}</el-descriptions-item>
          <el-descriptions-item label="物流公司">{{ detail.delivery ? (detail.delivery.logisticsCompany || '-') : '-' }}</el-descriptions-item>
          <el-descriptions-item label="物流单号">{{ detail.delivery ? (detail.delivery.logisticsNo || '-') : '-' }}</el-descriptions-item>
          <el-descriptions-item label="发货备注">{{ detail.delivery ? (detail.delivery.remark || '-') : '-' }}</el-descriptions-item>
          <el-descriptions-item label="发货时间">{{ detail.order.deliveryTime || '-' }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="detail.afterSales && detail.afterSales.length" class="section-title">售后记录</div>
        <el-table v-if="detail.afterSales && detail.afterSales.length" :data="detail.afterSales" border style="width: 100%">
          <el-table-column prop="afterSaleNo" label="售后单号" width="180" />
          <el-table-column prop="afterSaleType" label="售后类型" width="120" align="center">
            <template slot-scope="{ row }">
              {{ afterSaleTypeText(row.afterSaleType) }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="审核状态" width="100" align="center">
            <template slot-scope="{ row }">
              {{ afterSaleFlowText(row.status) }}
            </template>
          </el-table-column>
          <el-table-column label="换货目标规格" width="180" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ exchangeSkuText(row) }}
            </template>
          </el-table-column>
          <el-table-column prop="reasonDesc" label="售后原因" min-width="160" show-overflow-tooltip />
          <el-table-column prop="applyQuantity" label="申请数量" width="90" align="center" />
          <el-table-column prop="applyPoints" label="申请退积分" width="110" align="right" />
          <el-table-column prop="refundPoints" label="已退积分" width="100" align="right" />
          <el-table-column prop="returnLogisticsCompany" label="寄回物流" width="190" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ row.returnLogisticsCompany ? `${row.returnLogisticsCompany}/${row.returnLogisticsNo || '-'}` : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="returnLogisticsTime" label="寄回时间" width="170" />
          <el-table-column prop="returnReceiveTime" label="收货时间" width="170" />
          <el-table-column label="报废状态" width="110" align="center">
            <template slot-scope="{ row }">
              {{ scrapStatusText(row.scrapStatus) }}
            </template>
          </el-table-column>
          <el-table-column prop="exchangeOrderNo" label="换货订单" width="180" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ row.exchangeOrderNo || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="adminRemark" label="审核说明" min-width="160" show-overflow-tooltip />
          <el-table-column
            v-if="$hasPermission('mall:order:after-sale-audit')"
            label="操作"
            width="180"
            fixed="right"
            align="center"
          >
            <template slot-scope="{ row }">
              <el-tooltip
                :disabled="canAuditAfterSale(row)"
                :content="getAfterSaleAuditDisabledReason(row)"
                placement="top"
              >
                <span class="action-button-wrap">
                  <el-button
                    size="mini"
                    type="danger"
                    :disabled="!canAuditAfterSale(row)"
                    @click="openAfterSaleAudit(row)"
                  >
                    审核
                  </el-button>
                </span>
              </el-tooltip>
              <el-tooltip
                v-if="$hasPermission('mall:order:after-sale-receive')"
                :disabled="canConfirmAfterSaleReceive(row)"
                :content="getConfirmReceiveDisabledReason(row)"
                placement="top"
              >
                <span class="action-button-wrap">
                  <el-button
                    size="mini"
                    type="success"
                    :disabled="!canConfirmAfterSaleReceive(row)"
                    @click="confirmAfterSaleReceive(row)"
                  >
                    确认收货
                  </el-button>
                </span>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="订单发货" :visible.sync="deliveryVisible" width="560px" :close-on-click-modal="false">
      <el-form ref="deliveryFormRef" :model="deliveryForm" :rules="deliveryRules" label-width="90px">
        <el-form-item label="订单号">
          <el-input :value="deliveryTarget.orderNo || ''" disabled />
        </el-form-item>
        <el-form-item label="收货人">
          <el-input :value="detail.address ? detail.address.receiverName : ''" disabled />
        </el-form-item>
        <el-form-item label="物流公司" prop="logisticsCompany">
          <el-input v-model.trim="deliveryForm.logisticsCompany" placeholder="请输入物流公司" />
        </el-form-item>
        <el-form-item label="物流单号" prop="logisticsNo">
          <el-input v-model.trim="deliveryForm.logisticsNo" placeholder="请输入物流单号" />
        </el-form-item>
        <el-form-item label="发货备注">
          <el-input v-model.trim="deliveryForm.remark" type="textarea" :rows="3" placeholder="请输入发货备注" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="deliveryVisible = false">取消</el-button>
        <el-button type="primary" :loading="deliveryLoading" @click="submitDelivery">确认发货</el-button>
      </span>
    </el-dialog>

    <el-dialog title="物流轨迹" :visible.sync="logisticsVisible" width="640px">
      <div v-if="logisticsInfo.hasLogistics" class="logistics-panel">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单号">{{ logisticsInfo.orderNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="物流公司">{{ logisticsInfo.logisticsCompany || '-' }}</el-descriptions-item>
          <el-descriptions-item label="物流单号">{{ logisticsInfo.logisticsNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="查询来源">快递100接口预留</el-descriptions-item>
        </el-descriptions>
        <el-timeline class="logistics-timeline">
          <el-timeline-item
            v-for="(item, index) in logisticsInfo.traces"
            :key="index"
            :timestamp="item.time"
            :type="index === 0 ? 'primary' : ''"
          >
            {{ item.context }}
          </el-timeline-item>
        </el-timeline>
      </div>
      <el-empty v-else description="暂无物流信息" />
      <span slot="footer">
        <el-button @click="logisticsVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="订单取消" :visible.sync="cancelVisible" width="520px" :close-on-click-modal="false">
      <el-form ref="cancelFormRef" :model="cancelForm" :rules="cancelRules" label-width="90px">
        <el-form-item label="订单号">
          <el-input :value="cancelTarget.orderNo || ''" disabled />
        </el-form-item>
        <el-form-item label="当前状态">
          <el-input :value="orderStatusText(cancelTarget.orderStatus)" disabled />
        </el-form-item>
        <el-form-item label="取消原因" prop="cancelReason">
          <el-input v-model.trim="cancelForm.cancelReason" type="textarea" :rows="4" placeholder="请输入取消原因" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="cancelVisible = false">返回</el-button>
        <el-button type="warning" :loading="cancelLoading" @click="submitCancel">确认取消</el-button>
      </span>
    </el-dialog>

    <el-dialog title="售后审核" :visible.sync="auditVisible" width="600px" :close-on-click-modal="false">
      <el-form ref="auditFormRef" :model="auditForm" :rules="auditRules" label-width="100px">
        <el-form-item label="售后单号">
          <el-input :value="auditTarget.afterSaleNo || ''" disabled />
        </el-form-item>
        <el-form-item label="关联订单">
          <el-input :value="auditTarget.orderNo || ''" disabled />
        </el-form-item>
        <el-form-item label="售后类型">
          <el-input :value="afterSaleTypeText(auditTarget.afterSaleType)" disabled />
        </el-form-item>
        <el-form-item v-if="Number(auditTarget.afterSaleType) === 3" label="换货规格">
          <el-input :value="exchangeSkuText(auditTarget)" disabled />
        </el-form-item>
        <el-form-item label="申请退积分">
          <el-input :value="String(auditTarget.applyPoints || 0)" disabled />
        </el-form-item>
        <el-form-item label="售后凭证">
          <div v-if="auditTarget.imageUrls && auditTarget.imageUrls.length" class="after-sale-proof-list">
            <el-image
              v-for="img in auditTarget.imageUrls"
              :key="img"
              :src="resolveFileUrl(img)"
              :preview-src-list="auditImagePreviewList()"
              fit="cover"
              class="after-sale-proof-image"
            />
          </div>
          <span v-else class="empty-proof">暂无凭证</span>
        </el-form-item>
        <el-form-item label="审核结果" prop="auditStatus">
          <el-radio-group v-model="auditForm.auditStatus">
            <el-radio :label="1">审核通过</el-radio>
            <el-radio :label="2">驳回</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="showScrapOption" label="是否报废" prop="scrapStatus">
          <el-radio-group v-model="auditForm.scrapStatus">
            <el-radio :label="1">报废，不回滚库存</el-radio>
            <el-radio :label="0">不报废，回滚库存</el-radio>
          </el-radio-group>
          <div class="form-tip">退货或换货审核通过时生效；取消报废后可在报废管理中回滚库存。</div>
        </el-form-item>
        <el-form-item v-if="auditForm.auditStatus === 2" label="驳回原因" prop="rejectReason">
          <el-input v-model.trim="auditForm.rejectReason" type="textarea" :rows="3" placeholder="请输入驳回原因" />
        </el-form-item>
        <el-form-item label="审核说明">
          <el-input v-model.trim="auditForm.adminRemark" type="textarea" :rows="3" placeholder="请输入审核说明" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="auditVisible = false">取消</el-button>
        <el-button type="primary" :loading="auditLoading" @click="submitAfterSaleAudit">确认提交</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../../utils/http'
import { resolveFileUrl } from '../../utils/file'

function defaultQuery () {
  return {
    page: 1,
    limit: 10,
    orderNo: '',
    deliveryType: null,
    orderStatus: null
  }
}

function defaultDeliveryForm () {
  return {
    orderId: null,
    logisticsCompany: '',
    logisticsNo: '',
    remark: ''
  }
}

function defaultCancelForm () {
  return {
    orderId: null,
    cancelReason: ''
  }
}

function defaultAuditForm () {
  return {
    afterSaleId: null,
    auditStatus: 1,
    scrapStatus: 1,
    rejectReason: '',
    adminRemark: ''
  }
}

export default {
  data () {
    return {
      query: defaultQuery(),
      total: 0,
      list: [],
      detailVisible: false,
      detail: {},
      deliveryVisible: false,
      deliveryLoading: false,
      deliveryForm: defaultDeliveryForm(),
      deliveryTarget: {},
      cancelVisible: false,
      cancelLoading: false,
      cancelForm: defaultCancelForm(),
      cancelTarget: {},
      auditVisible: false,
      auditLoading: false,
      auditForm: defaultAuditForm(),
      auditTarget: {},
      logisticsVisible: false,
      logisticsInfo: {
        hasLogistics: false,
        orderNo: '',
        logisticsCompany: '',
        logisticsNo: '',
        traces: []
      },
      orderStatusOptions: [
        { value: 1, label: '待发货' },
        { value: 2, label: '待收货' },
        { value: 3, label: '已完成' },
        { value: 4, label: '已取消' },
        { value: 5, label: '售后中' },
        { value: 6, label: '已退款' }
      ],
      deliveryRules: {
        logisticsCompany: [{ required: true, message: '请输入物流公司', trigger: 'blur' }],
        logisticsNo: [{ required: true, message: '请输入物流单号', trigger: 'blur' }]
      },
      cancelRules: {
        cancelReason: [{ required: true, message: '请输入取消原因', trigger: 'blur' }]
      },
      auditRules: {
        auditStatus: [{ required: true, message: '请选择审核结果', trigger: 'change' }],
        scrapStatus: [{ required: true, message: '请选择是否报废', trigger: 'change' }],
        rejectReason: [{
          validator: (rule, value, callback) => {
            if (this.auditForm.auditStatus === 2 && !value) {
              callback(new Error('请输入驳回原因'))
              return
            }
            callback()
          },
          trigger: 'blur'
        }]
      }
    }
  },
  computed: {
    showScrapOption () {
      const type = Number(this.auditTarget.afterSaleType)
      return Number(this.auditForm.auditStatus) === 1 && (type === 2 || type === 3)
    }
  },
  watch: {
    '$route.query.orderId' () {
      this.openDetailFromRoute()
    }
  },
  created () {
    this.load().then(() => this.openDetailFromRoute())
  },
  methods: {
    resolveFileUrl,
    auditImagePreviewList () {
      return ((this.auditTarget && this.auditTarget.imageUrls) || [])
        .filter(Boolean)
        .map(item => this.resolveFileUrl(item))
    },
    async load () {
      const { data } = await http.get('/mall/order/list', { params: this.query })
      const pageData = data.data || {}
      this.list = pageData.list || []
      this.total = Number(pageData.total || 0)
    },
    handleSearch () {
      this.query.page = 1
      this.load()
    },
    handlePageChange (page) {
      this.query.page = page
      this.load()
    },
    handleSizeChange (limit) {
      this.query.limit = limit
      this.query.page = 1
      this.load()
    },
    reset () {
      this.query = defaultQuery()
      this.load()
    },
    orderStatusText (value) {
      const map = { 0: '待处理', 1: '待发货', 2: '待收货', 3: '已完成', 4: '已取消', 5: '售后中', 6: '已退款' }
      return map[value] || '-'
    },
    discountInfoText (row) {
      if (!row) return '-'
      const levelName = row.memberLevelName || '普通会员'
      return `${levelName} / ${this.discountText(row.discountRate)}`
    },
    discountText (value) {
      if (value === undefined || value === null || value === '') return '-'
      const rate = Number(value)
      if (!Number.isFinite(rate)) return '-'
      if (rate === 10 || rate === 100) return '无折扣'
      const discount = rate > 10 ? rate / 10 : rate
      return `${Number(discount.toFixed(2))}折`
    },
    deliveryStatusText (value) {
      const map = { 0: '未发货', 1: '已发货', 2: '已签收' }
      return map[value] || '-'
    },
    issueStatusText (row) {
      if (!row) return '-'
      if (Number(row.deliveryType) === 2) {
        if (Number(row.orderStatus) === 4) return '已取消'
        if (Number(row.orderStatus) === 6) return '已退款'
        return Number(row.orderStatus) === 3 ? '已自动发放' : '待自动发放'
      }
      return this.deliveryStatusText(row.deliveryStatus)
    },
    afterSaleStatusText (value) {
      const map = { 0: '无售后', 1: '售后中', 2: '售后完成' }
      return map[value] || '-'
    },
    afterSaleTypeText (value) {
      const map = { 1: '退积分', 2: '退货退积分', 3: '换货' }
      return map[value] || '-'
    },
    exchangeSkuText (row) {
      if (!row || Number(row.afterSaleType) !== 3) return '-'
      return row.exchangeSpecText || row.exchangeSkuName || row.exchangeSkuNo || '-'
    },
    virtualIssueContent () {
      const items = Array.isArray(this.detail.items) ? this.detail.items : []
      const cdkLines = items
        .filter(item => item && item.cdkCodes)
        .map(item => {
          const goodsName = item.goodsName || '虚拟商品'
          return `${goodsName}：${item.cdkCodes}`
        })
      if (cdkLines.length) {
        return cdkLines.join('\n')
      }
      return this.detail.order && this.detail.order.virtualContent ? this.detail.order.virtualContent : '-'
    },
    afterSaleFlowText (value) {
      const map = { 0: '待审核', 1: '待用户寄回', 2: '已驳回', 3: '待确认收货', 4: '已完成', 5: '已取消' }
      return map[value] || '-'
    },
    scrapStatusText (value) {
      const map = { 0: '未报废', 1: '已报废' }
      return map[value] || '-'
    },
    canDelivery (row) {
      return row && Number(row.deliveryType) === 1 && Number(row.orderStatus) === 1 && Number(row.deliveryStatus || 0) === 0
    },
    canCancel (row) {
      return row && Number(row.orderStatus) === 1 && Number(row.deliveryStatus || 0) === 0
    },
    canViewLogistics (row) {
      return row && Number(row.deliveryType) === 1 && Number(row.orderStatus) !== 4
    },
    canAuditOrder (row) {
      return row && Number(row.afterSaleStatus || 0) === 1
    },
    canAuditAfterSale (row) {
      return row && Number(row.status) === 0
    },
    canConfirmAfterSaleReceive (row) {
      const type = Number(row && row.afterSaleType)
      return row && (type === 2 || type === 3) && Number(row.status) === 3 && !!row.returnLogisticsCompany && !!row.returnLogisticsNo
    },
    getDeliveryDisabledReason (row) {
      if (!row) return '当前订单数据异常'
      if (Number(row.deliveryType) !== 1) return '虚拟订单不支持发货'
      if (Number(row.orderStatus) !== 1) return '仅待发货订单可发货'
      if (Number(row.deliveryStatus || 0) !== 0) return '订单已发货，不能重复发货'
      return ''
    },
    getCancelDisabledReason (row) {
      if (!row) return '当前订单数据异常'
      if (Number(row.orderStatus) !== 1) return '仅待发货订单可取消'
      if (Number(row.deliveryStatus || 0) !== 0) return '订单已发货，不能取消'
      return ''
    },
    getOrderAuditDisabledReason (row) {
      if (!row) return '当前订单数据异常'
      if (Number(row.afterSaleStatus || 0) !== 1) return '当前订单没有待处理售后'
      return ''
    },
    getAfterSaleAuditDisabledReason (row) {
      if (!row) return '当前售后数据异常'
      if (Number(row.status) !== 0) return '该售后记录已处理，不能重复审核'
      return ''
    },
    getConfirmReceiveDisabledReason (row) {
      if (!row) return '当前售后数据异常'
      const type = Number(row.afterSaleType)
      if (type !== 2 && type !== 3) return '仅退货或换货需要确认寄回收货'
      if (Number(row.status) !== 3) return '请等待用户填写寄回物流'
      if (!row.returnLogisticsCompany || !row.returnLogisticsNo) return '用户尚未填写寄回物流'
      return ''
    },
    async fetchDetail (id) {
      const { data } = await http.get(`/mall/order/info/${id}`)
      this.detail = data.data || {}
      return this.detail
    },
    async openDetail (row, forceVisible = true) {
      await this.fetchDetail(row.id)
      if (forceVisible) {
        this.detailVisible = true
      }
    },
    async openDetailFromRoute () {
      const orderId = this.$route.query.orderId
      if (!orderId) {
        return
      }
      await this.fetchDetail(orderId)
      this.detailVisible = true
    },
    async openLogistics (row) {
      if (!row || !row.id) {
        this.logisticsInfo = this.buildEmptyLogisticsInfo()
        this.logisticsVisible = true
        return
      }
      const detail = await this.fetchDetail(row.id)
      this.logisticsInfo = this.buildLogisticsInfo(detail)
      this.logisticsVisible = true
    },
    buildEmptyLogisticsInfo () {
      return {
        hasLogistics: false,
        orderNo: '',
        logisticsCompany: '',
        logisticsNo: '',
        traces: []
      }
    },
    buildLogisticsInfo (detail) {
      const order = detail && detail.order ? detail.order : {}
      const delivery = detail && detail.delivery ? detail.delivery : {}
      if (!delivery.logisticsNo) {
        return this.buildEmptyLogisticsInfo()
      }
      return {
        hasLogistics: true,
        orderNo: order.orderNo || delivery.orderNo || '',
        logisticsCompany: delivery.logisticsCompany || '',
        logisticsNo: delivery.logisticsNo || '',
        traces: [
          { time: order.deliveryTime || delivery.shipTime || '-', context: '商家已发货，物流信息已录入系统' },
          { time: '-', context: '快递100物流轨迹接口预留，后续对接后展示实时运输节点' },
          { time: '-', context: '包裹等待承运商更新物流轨迹' }
        ]
      }
    },
    async openDelivery (row) {
      await this.fetchDetail(row.id)
      this.deliveryTarget = row
      this.deliveryForm = defaultDeliveryForm()
      this.deliveryForm.orderId = row.id
      if (this.detail.delivery) {
        this.deliveryForm.logisticsCompany = this.detail.delivery.logisticsCompany || ''
        this.deliveryForm.logisticsNo = this.detail.delivery.logisticsNo || ''
        this.deliveryForm.remark = this.detail.delivery.remark || ''
      }
      this.deliveryVisible = true
      this.$nextTick(() => {
        this.$refs.deliveryFormRef && this.$refs.deliveryFormRef.clearValidate()
      })
    },
    submitDelivery () {
      this.$refs.deliveryFormRef.validate(async valid => {
        if (!valid) return
        this.deliveryLoading = true
        try {
          const { data } = await http.post('/mall/order/delivery', this.deliveryForm)
          if (data.code === 0) {
            this.$message.success('订单发货成功')
            this.deliveryVisible = false
            await this.load()
            if (this.detail.order && this.detail.order.id === this.deliveryForm.orderId) {
              await this.fetchDetail(this.deliveryForm.orderId)
            }
          }
        } finally {
          this.deliveryLoading = false
        }
      })
    },
    openCancel (row) {
      this.cancelTarget = row
      this.cancelForm = defaultCancelForm()
      this.cancelForm.orderId = row.id
      this.cancelVisible = true
      this.$nextTick(() => {
        this.$refs.cancelFormRef && this.$refs.cancelFormRef.clearValidate()
      })
    },
    submitCancel () {
      this.$refs.cancelFormRef.validate(async valid => {
        if (!valid) return
        this.cancelLoading = true
        try {
          const { data } = await http.post('/mall/order/cancel', this.cancelForm)
          if (data.code === 0) {
            this.$message.success('订单取消成功')
            this.cancelVisible = false
            await this.load()
            if (this.detail.order && this.detail.order.id === this.cancelForm.orderId) {
              await this.fetchDetail(this.cancelForm.orderId)
            }
          }
        } finally {
          this.cancelLoading = false
        }
      })
    },
    openAfterSaleAudit (row) {
      this.auditTarget = row
      this.auditForm = defaultAuditForm()
      this.auditForm.afterSaleId = row.id
      this.auditForm.auditStatus = 1
      this.auditVisible = true
      this.$nextTick(() => {
        this.$refs.auditFormRef && this.$refs.auditFormRef.clearValidate()
      })
    },
    submitAfterSaleAudit () {
      this.$refs.auditFormRef.validate(async valid => {
        if (!valid) return
        if (this.showScrapOption && this.auditForm.scrapStatus === null) {
          this.$message.warning('请选择是否报废')
          return
        }
        this.auditLoading = true
        try {
          const { data } = await http.post('/mall/order/after-sale/audit', this.auditForm)
          if (data.code === 0) {
            this.$message.success('售后审核提交成功')
            this.auditVisible = false
            await this.load()
            if (this.detail.order && this.auditTarget.orderId) {
              await this.fetchDetail(this.auditTarget.orderId)
            }
          }
        } finally {
          this.auditLoading = false
        }
      })
    },
    async confirmAfterSaleReceive (row) {
      try {
        await this.$confirm('确认已收到用户寄回商品？确认后将执行退积分或生成换货订单。', '确认收货', {
          type: 'warning'
        })
      } catch (error) {
        return
      }
      const { data } = await http.post(`/mall/order/after-sale/confirm-receive/${row.id}`)
      if (data.code === 0) {
        this.$message.success('售后收货确认成功')
        await this.load()
        if (this.detail.order && row.orderId) {
          await this.fetchDetail(row.orderId)
        }
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.search-wrap {
  flex-wrap: wrap;
}

.operation-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  justify-items: stretch;
  padding: 4px 0;
}

.operation-grid .el-button,
.operation-grid .action-button-wrap,
.operation-grid .action-button-wrap .el-button {
  width: 100%;
  margin: 0;
}

.operation-grid__wide {
  grid-column: 1 / span 2;
}

.logistics-op-btn {
  color: #46627f;
  background: #edf4fb;
  border-color: #c8d8e8;
}

.logistics-op-btn:hover,
.logistics-op-btn:focus {
  color: #2f5f8f;
  background: #e4f0fb;
  border-color: #9fbedc;
}

.action-button-wrap {
  display: inline-block;
}

.section-title {
  margin: 16px 0 10px;
  font-weight: 600;
  color: #303133;
}

.order-detail-dialog ::v-deep .order-detail-desc .el-descriptions__table {
  table-layout: fixed;
}

.order-detail-dialog ::v-deep .order-detail-desc .el-descriptions-item__label {
  width: 96px;
  min-width: 96px;
  color: #8a9099;
}

.order-detail-dialog ::v-deep .order-detail-desc .el-descriptions-item__content {
  min-width: 0;
  word-break: break-word;
}

.pre-line-text {
  white-space: pre-line;
}

.form-tip {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.after-sale-proof-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.after-sale-proof-image {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  cursor: pointer;
}

.empty-proof {
  color: #909399;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.logistics-panel {
  padding-top: 4px;
}

.logistics-timeline {
  margin-top: 20px;
}
</style>
